import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, asArray, formatApiError } from "@/lib/api";
import { pesoOr } from "@/lib/money";
import { AlertTriangle, ArrowRight, RefreshCw, Search } from "lucide-react";

// How many rows this screen draws before it stops and says how many there are.
// ⚠️ THE TABLE HAD NO CAP AT ALL: 320 rows measured 19,057px tall, and the
// route returns up to 500. A page nobody can scroll to the bottom of hides its
// own totals, and the blocked-reason panel that explains the whole screen sits
// above it.
const PAGE = 50;

// Approved, billable work that no billing run has claimed -- the question that
// costs an MSP the most money, which until now had no screen at all.
//
// ⚠️ **THIS SCREEN DOES NOT OFFER TO CREATE A RUN FOR A ROW THAT CANNOT BE
// BILLED.** On production every one of the 306 unbilled entries is unpriced,
// so a one-click "bill this" would create an EMPTY draft for that client and
// month, and the unique index would then refuse a second run for it. The draft
// is releasable, so it is recoverable, but the screen would be inviting an
// action that makes the situation worse and then telling the operator the
// period is closed. A blocked row says WHY instead, in the API's own words.
//
// ⚠️ And the link that IS offered creates nothing. It carries the client and
// the period over to the Billing Runs screen with the form prefilled; a person
// still presses Create there.

const REASON_LABEL = {
  unpriced: "No amount recorded",
  amount_not_an_integer: "Amount is not a whole number of centavos",
  amount_negative: "Amount is negative",
};

function reasonLabel(key) {
  return REASON_LABEL[key] || String(key || "unknown").replaceAll("_", " ");
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

export default function UnbilledWork() {
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  // Draft filter state, applied on submit rather than on every keystroke: the
  // report is a full scan of the tenant's unbilled work.
  const [clientId, setClientId] = useState("");
  const [minAge, setMinAge] = useState("0");
  const [applied, setApplied] = useState({ client_id: "", min_age_days: "0" });
  const [shown, setShown] = useState(PAGE);

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (applied.client_id) q.set("client_id", applied.client_id);
    if (applied.min_age_days && applied.min_age_days !== "0") q.set("min_age_days", applied.min_age_days);
    const qs = q.toString();
    api.get(`/billing/unbilled${qs ? `?${qs}` : ""}`)
      .then((r) => { setData(r.data); setErr(""); setShown(PAGE); })
      .catch((e) => { setData(null); setErr(formatApiError(e)); })
      .finally(() => setLoading(false));
  }, [applied]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(asArray(r.data))).catch(() => setClients([]));
  }, []);

  const items = asArray(data?.items);
  const visible = items.slice(0, shown);
  const totals = data?.totals || {};
  const blocked = data?.blocked_by_reason || {};
  const blockedKeys = Object.keys(blocked);

  return (
    <div className="space-y-4" data-testid="unbilled-work-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Billing · Preparation</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Unbilled Work</h1>
          <div className="text-xs text-zinc-500 mt-1">
            Approved, billable time that no billing run has claimed. Nothing removes a row but billing it: there is no dismiss and no age-out.
          </div>
        </div>
        <button onClick={load} data-testid="unbilled-refresh"
          className="border border-zinc-800 rounded-md px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-900 flex items-center gap-1.5">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* The server's own headline, verbatim. It already says how much is
          waiting, how much of it cannot be billed today and why, and what to do
          about it -- and it is computed from the same counts the table below
          renders, so a sentence here cannot drift from the rows. */}
      {data?.headline && (
        <div className="border border-zinc-800 bg-zinc-950 rounded-md px-4 py-3 text-sm text-zinc-200 leading-relaxed" data-testid="unbilled-headline">
          {data.headline}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end" data-testid="unbilled-filters">
        <label className="text-xs text-zinc-500 space-y-1">
          <div>Client</div>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)}
            data-testid="unbilled-filter-client"
            className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 min-w-[14rem]">
            <option value="">Every client</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="text-xs text-zinc-500 space-y-1">
          <div>Older than (days)</div>
          <input value={minAge} onChange={(e) => setMinAge(e.target.value)} inputMode="numeric"
            data-testid="unbilled-filter-age"
            className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 font-mono w-24" />
        </label>
        <button onClick={() => setApplied({ client_id: clientId, min_age_days: minAge })}
          data-testid="unbilled-filter-apply"
          className="border border-zinc-800 rounded-md px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-900 flex items-center gap-1.5">
          <Search size={14} /> Apply
        </button>
      </div>

      {err && <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm" data-testid="unbilled-error">{err}</div>}
      {loading && !err && <div className="text-zinc-600 text-sm">Loading…</div>}

      {/* ⚠️ THE AGE FILTER IS WHAT HID 306 ROWS ONCE. A report that says
          "nothing is waiting" while an age filter is removing rows is saying
          two opposite things with one empty list, so the count it removed is
          printed whenever it is non-zero. */}
      {totals.excluded_by_age > 0 && (
        <div className="border border-amber-500/40 bg-amber-950/20 text-amber-300 rounded px-4 py-3 text-sm" data-testid="unbilled-age-excluded">
          The age filter is hiding {plural(totals.excluded_by_age, "row", "rows")} that are newer than {applied.min_age_days} days. They are unbilled too. Set it to 0 to see everything.
        </div>
      )}

      {data?.truncated && (
        <div className="border border-amber-500/40 bg-amber-950/20 text-amber-300 rounded px-4 py-3 text-sm" data-testid="unbilled-truncated">
          This page shows {items.length} of {totals.rows} rows{totals.complete === false ? ", and the scan itself did not reach the end, so the money totals below are over the rows it read rather than over all of them" : ""}. Filter by client to see the rest.
        </div>
      )}

      {!loading && !err && data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="unbilled-totals">
          <Stat label="Rows waiting" value={String(totals.rows ?? 0)}
            sub={`${totals.billable_now ?? 0} can be billed now, ${totals.blocked ?? 0} cannot`} />
          <Stat label="Worth" value={pesoOr(totals.amount_minor, "Nothing that can be totalled")}
            sub="Over the rows that can be billed now. Blocked rows carry no amount to add." />
          <Stat label="Clients" value={String(totals.clients ?? 0)}
            sub={totals.oldest_occurred_on ? `Oldest work ${totals.oldest_occurred_on}` : "No dated work"} />
          <Stat label="Untraceable rows" value={String(totals.rows_without_ticket_number ?? 0)}
            sub={totals.rows_without_ticket_number ? "These name no ticket number, so a client cannot be shown what the work was" : "Every row names its ticket"} />
        </div>
      )}

      {/* Why the blocked rows are blocked, once, above the table -- rather than
          the same sentence repeated down a column. */}
      {!!blockedKeys.length && (
        <div className="border border-amber-500/40 bg-amber-950/20 rounded-md p-4 space-y-3" data-testid="unbilled-blocked">
          <div className="flex items-center gap-2 text-amber-300 font-medium text-sm">
            <AlertTriangle size={16} /> {plural(totals.blocked ?? 0, "row", "rows")} cannot be billed today
          </div>
          {blockedKeys.map((k) => (
            <div key={k} className="space-y-1" data-testid={`unbilled-blocked-${k}`}>
              <div className="text-sm text-amber-200">
                {reasonLabel(k)}: {plural(blocked[k].count, "row", "rows")}
                {blocked[k].clients ? ` across ${plural(blocked[k].clients, "client", "clients")}` : ""}
                {blocked[k].oldest_occurred_on ? `, oldest ${blocked[k].oldest_occurred_on}` : ""}
              </div>
              <div className="text-xs text-amber-200/80 leading-relaxed">{blocked[k].detail}</div>
            </div>
          ))}
          <div className="text-[11px] text-amber-300/70 leading-relaxed">
            Creating a billing run for a client whose work is all blocked produces an EMPTY draft and closes that month: no route adds a line to an existing run and a second run for the same client and period is refused. Releasing the draft is the way back, and it only works while it is still a draft.
          </div>
        </div>
      )}

      {!loading && !err && data && !items.length && (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md px-4 py-8 text-sm text-zinc-400" data-testid="unbilled-empty">
          {data.vacuous
            ? "This tenant holds no approved, billable time at all, so there is nothing here to bill. That is not the same as everything being billed: nobody has logged and approved chargeable work yet."
            : "Every approved, billable hour has been claimed by a billing run. Nothing is waiting."}
        </div>
      )}

      {!!items.length && (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden" data-testid="unbilled-table">
          {items.length > visible.length && (
            <div className="px-4 py-2.5 border-b border-zinc-900 text-[11px] text-zinc-500" data-testid="unbilled-cap">
              Showing the first {visible.length} of {items.length} rows on this page.{" "}
              <button onClick={() => setShown(items.length)} data-testid="unbilled-show-all"
                className="text-blue-400 hover:underline">Show all {items.length}</button>
            </div>
          )}
          <table className="w-full text-sm hidden lg:table">
            <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Age</th>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Ticket</th>
                <th className="text-left px-4 py-3">Work</th>
                <th className="text-left px-4 py-3">Minutes</th>
                <th className="text-left px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Next</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((it) => (
                <tr key={it.entry_id} data-testid={`unbilled-row-${it.entry_id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40 align-top">
                  <td className="px-4 py-2 font-mono text-zinc-400">{it.occurred_on || "No date recorded"}</td>
                  <td className="px-4 py-2 font-mono text-zinc-500">
                    {it.age_days === null || it.age_days === undefined ? "Not datable" : `${it.age_days}d`}
                  </td>
                  <td className="px-4 py-2 text-zinc-300">{it.client_name || it.client_id || "No client recorded"}</td>
                  <td className="px-4 py-2">
                    {it.ticket_id
                      ? <Link to={`/tickets/${it.ticket_id}`} className="text-zinc-300 hover:text-blue-400">
                          #{it.ticket_number ?? "number unknown"} {it.ticket_title || ""}
                        </Link>
                      : <span className="text-zinc-500">No ticket recorded</span>}
                  </td>
                  <td className="px-4 py-2 text-zinc-400">
                    {it.work_type || "No work type recorded"}
                    <div className="text-[11px] text-zinc-600">{it.user_name || "Technician not recorded"}</div>
                  </td>
                  <td className="px-4 py-2 font-mono text-zinc-400">{it.minutes ?? "Not recorded"}</td>
                  <td className="px-4 py-2 font-mono text-zinc-100">
                    {/* ⚠️ THE API RETURNS `amount_minor: null` ON A BLOCKED ROW
                        DELIBERATELY: a number in a money column that no total
                        includes invites somebody to add the column up by hand
                        and get a different answer. The reason goes here
                        instead. */}
                    {pesoOr(it.amount_minor, <span className="text-amber-300 font-sans text-xs">{reasonLabel(it.blocked_reason)}</span>)}
                  </td>
                  <td className="px-4 py-2 text-xs max-w-md"><NextStep item={it} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="lg:hidden divide-y divide-zinc-900">
            {visible.map((it) => (
              <div key={it.entry_id} className="p-4 space-y-1.5" data-testid={`unbilled-card-${it.entry_id}`}>
                <div className="text-zinc-100">{it.client_name || it.client_id || "No client recorded"}</div>
                <div className="text-[11px] text-zinc-500 font-mono">
                  {it.occurred_on || "No date recorded"} · {it.age_days === null || it.age_days === undefined ? "not datable" : `${it.age_days}d old`} · {it.minutes ?? "?"} min
                </div>
                <div className="text-xs text-zinc-400">
                  {it.ticket_id
                    ? <Link to={`/tickets/${it.ticket_id}`} className="hover:text-blue-400">#{it.ticket_number ?? "number unknown"} {it.ticket_title || ""}</Link>
                    : "No ticket recorded"}
                </div>
                <div className="text-sm font-mono text-zinc-100">
                  {pesoOr(it.amount_minor, <span className="text-amber-300 font-sans text-xs">{reasonLabel(it.blocked_reason)}</span>)}
                </div>
                <div className="text-xs"><NextStep item={it} /></div>
              </div>
            ))}
          </div>
          {items.length > visible.length && (
            <div className="px-4 py-2 text-[11px] text-zinc-600 border-t border-zinc-900">
              {items.length - visible.length} more {items.length - visible.length === 1 ? "row is" : "rows are"} not drawn above. The totals and the blocked-reason panel are over every row, not just these.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// The only place this screen says what to do about a row.
//
// ⚠️ A CLAIMABLE ROW GETS A LINK, A BLOCKED ROW GETS A SENTENCE. The link
// carries the client and the period to the Billing Runs form; it creates
// nothing. A blocked row gets `detail` from the API, which already names only
// remedies that exist -- this screen adds none of its own.
function NextStep({ item }) {
  if (item.billable_now && item.client_id && item.period) {
    return (
      <Link data-testid={`unbilled-bill-${item.entry_id}`}
        to={`/billing/runs?client_id=${encodeURIComponent(item.client_id)}&period=${encodeURIComponent(item.period)}`}
        className="text-blue-400 hover:underline inline-flex items-center gap-1">
        Prepare {item.period} for this client <ArrowRight size={12} />
      </Link>
    );
  }
  if (item.billable_now) {
    return <span className="text-zinc-500">This row can be billed, but it records no client or no month, so no run can be addressed to it. Fix the entry first.</span>;
  }
  return <span className="text-amber-300/90">{item.detail || `This row cannot be billed: ${reasonLabel(item.blocked_reason)}.`}</span>;
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className="text-2xl font-display mt-1 text-zinc-100">{value}</div>
      {sub ? <div className="text-xs text-zinc-500 mt-1">{sub}</div> : null}
    </div>
  );
}
